/**
 * Decides when the checkpoint layers need `drawCheckpoints` again (docs/PERFORMANCE.md,
 * Phase 3). Gate paint depends on the zoom the dashes / checkers / poles were sized at,
 * the hover preview set, and the race state. Everything else that used to call
 * `repaintGates` every frame (auto-zoom easing during a move, hover refreshes with an
 * unchanged aim) is a no-op here.
 *
 * Colour-only repaints (a pole turning green, a preview turning yellow) keep the last
 * painted zoom so a colour change does not restroke at a new zoom; only the chrome
 * restroke (`redrawCheckpoints`) hands in the live camera zoom, and only after an 8% drift.
 */
import type { RaceState } from '../sim/race.ts';

export function gatePaintKey(
  zoom: number,
  previewIds: ReadonlySet<string>,
  race: Pick<RaceState, 'lap' | 'passedCheckpointIds' | 'complete'>,
): string {
  return [
    zoom.toFixed(4),
    [...previewIds].sort().join(','),
    [...race.passedCheckpointIds].sort().join(','),
    String(race.lap),
    race.complete ? '1' : '0',
  ].join('|');
}

export type GatePaintPlan = {
  paint: boolean;
  zoom: number;
};

export class GatePaintClock {
  private key = '';
  private zoom: number;
  private paints = 0;

  constructor(initialZoom = 1) {
    this.zoom = initialZoom;
  }

  /** Zoom the gates were last painted at (the `data-gates-zoom` probe). */
  get paintedZoom(): number {
    return this.zoom;
  }

  /** How many paints this clock has approved; tests and the Perf readout read it. */
  get paintCount(): number {
    return this.paints;
  }

  /**
   * `zoom` undefined keeps the painted scale (colour-only repaint). `force` repaints even
   * when nothing changed — used after the geometry cache or the map itself changed.
   */
  plan(
    previewIds: ReadonlySet<string>,
    race: Pick<RaceState, 'lap' | 'passedCheckpointIds' | 'complete'>,
    options: { zoom?: number; force?: boolean } = {},
  ): GatePaintPlan {
    const zoom = options.zoom ?? this.zoom;
    const key = gatePaintKey(zoom, previewIds, race);

    if (!options.force && key === this.key) {
      return { paint: false, zoom };
    }

    this.key = key;
    this.zoom = zoom;
    this.paints += 1;
    return { paint: true, zoom };
  }

  reset(zoom = 1): void {
    this.key = '';
    this.zoom = zoom;
    this.paints = 0;
  }
}
